import classNames from "classnames";
import {format, formatDistanceToNowStrict} from "date-fns";

import Button from "@/components/button/Button.tsx";
import {CDNImage} from "@/components/cdnImage/CDNImage.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {PublicProfile} from "@/lib/types/isitdown.ts";
import {OUTAGE_THRESHOLD} from "@/marketingPages/isitdown/components/reportedissues/PublicProfilesReportedIssues.tsx";

import {IsItDownStatusBadge} from "../isitdownstatusbadge/IsItDownStatusBadge.tsx";
import {SimilarServices} from "../similarservices/SimilarServices.tsx";

import styles from "./IsItDownDetailsHeader.module.scss";

interface IsItDownDetailsHeaderProps {
  profile: PublicProfile;
  openDownReportForm: () => void;
  lastCheckTimestamp?: Date;
  reportCount?: number;
  similarServices: PublicProfile[];
}

export const IsItDownDetailsHeader = ({
  profile,
  openDownReportForm,
  lastCheckTimestamp,
  reportCount,
  similarServices,
}: IsItDownDetailsHeaderProps) => {
  const profileName = profile.name || profile.slug;
  return (
    <div className={classNames(styles.container, "d-flex flex-column align-items-center")}>
      <h1 className="marketingShowOnMobile marketingTextH3Bold pulse-color-gray-100 m-0">Is {profileName} down?</h1>
      <div className={styles.header}>
        <div className={styles.topSection}>
          <div className={classNames(styles.image, "d-flex align-items-center justify-content-center flex-shrink-0")}>
            <CDNImage
              src={profile.image}
              domain={profile.slug}
              alt={`View ${profile.slug} outages and uptime`}
              title={profile.slug}
            />
          </div>
          <div className={classNames(styles.status, "d-flex flex-column justify-content-center")}>
            <h1 className="marketingTextCaptionSmall pulse-color-gray-700 m-0">Is {profileName} down?</h1>
            <div>
              <h2 className={classNames(styles.slug, "marketingTextS1Bold pulse-color-gray-900 m-0")}>{profileName}</h2>
              {profile.description && (
                <h3 className="marketingHideOnMobile marketingTextBodyTiny pulse-color-gray-600 mt-1 mb-0">
                  {profile.description}
                </h3>
              )}
            </div>
            <div className={classNames(styles.statusBadge, "d-flex align-items-center flex-wrap gap-2 mt-2")}>
              <IsItDownStatusBadge serviceStatus={profile.service_status} variant="header" />
              {lastCheckTimestamp && (
                <div className="marketingTextBodyTiny pulse-color-gray-700">
                  as of {formatDistanceToNowStrict(lastCheckTimestamp, {addSuffix: true})}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.bottomSection}>
          <Divider orientation="horizontal" className="w-100 marketingShowOnMobile" />
          {lastCheckTimestamp && (
            <>
              <div className={styles.lastCheck}>
                <div className="pulseTextBodySmall pulse-color-gray-700 mb-2">Last Checked</div>
                <div className="pulseTextBodyBold pulse-color-gray-900">
                  {format(lastCheckTimestamp, "PPP")}
                  <br className="marketingShowOnMobile" />
                  {format(lastCheckTimestamp, " 'at' p z")}
                </div>
              </div>
              <Divider orientation="horizontal" className="w-100 marketingShowOnMobile" />
              <Divider orientation="vertical" className="mx-2 marketingHideOnMobile" />
            </>
          )}
          {reportCount !== undefined && (
            <>
              <div>
                <div className="pulseTextBodySmall pulse-color-gray-700 mb-2">User Outage Reports – Last 24h</div>
                <div
                  className={classNames("pulseTextBodyBold", {
                    "pulse-color-red-500": reportCount >= OUTAGE_THRESHOLD,
                    "pulse-color-orange-500": reportCount > 0 && reportCount < OUTAGE_THRESHOLD,
                    "pulse-color-green-500": reportCount === 0,
                  })}
                >
                  {reportCount} Issue{reportCount !== 1 && "s"} Reported
                </div>
              </div>
              <Divider orientation="horizontal" className="w-100 marketingShowOnMobile" />
              <Divider orientation="vertical" className="mx-2 marketingHideOnMobile" />
            </>
          )}
          <Button variant="marketingPrimary" size="marketingLarge" onClick={() => openDownReportForm()}>
            Report an Issue
          </Button>
        </div>
      </div>
      <SimilarServices similarServices={similarServices} />
    </div>
  );
};
